import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, resolveApiAssetUrl } from '../lib/api'
import { formatDateTimeMinute, formatDate } from '../lib/date'
import { MENU_ITEMS, MENU_CATEGORIES, MENU_SURFACES, createMenuVisibilityContext, filterVisibleMenuItems, loadFavorites, saveFavorites } from '../lib/menu'
import { getSystemNoticeCategoryBadgeClass, getSystemNoticeCategoryLabel } from '../lib/systemNoticeCategory'
import {
    filterMonthlyBriefImagesByKind,
    findSlideMetaForMonthlyBriefImage,
    parseStorageSlideIdFromMonthlyBriefImageName,
    sortMonthlyBriefImagesBySlideOrder,
} from '../lib/monthlyBriefCardNews'

import DashboardCalendarPanel from '../components/DashboardCalendarPanel'
import { useNotifications } from '../ui/NotificationCenter'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { AlertTriangle, Bell, ChevronLeft, ChevronRight, Info, X, XCircle } from 'lucide-react'

// 배너 이미지 URL 캐시 (전역 캐시로 중복 요청 방지)
const bannerImageCache = new Map()
// 전역 Promise 캐시 (같은 fileId에 대한 동시 요청 시 같은 Promise 재사용)
const bannerImagePromises = new Map()

const ALERT_ICON_MAP = {
    warning: AlertTriangle,
    error: XCircle,
    info: Info,
}

const BRIEFING_CATEGORY_LABELS = {
    MATURITY: '만기',
    LEAD: '리드',
    CUSTOMER_EVENT: '고객 이벤트',
    CALENDAR: '일정',
}

function DailyWorkBriefingPanel({ briefing }) {
    const rawItems = Array.isArray(briefing?.items) ? briefing.items : []
    const items = rawItems.filter((item) => item?.category !== 'CALENDAR')
    const visibleLimit = 8
    const visibleItems = items.slice(0, visibleLimit)
    const hiddenCount = Math.max(0, items.length - visibleItems.length)
    const maturityCount = Number(briefing?.counts?.MATURITY || 0)
    const visibleMaturityCount = visibleItems.filter((item) => item?.category === 'MATURITY').length
    const hiddenMaturityCount = Math.max(0, maturityCount - visibleMaturityCount)
    if (!briefing) {
        return <div className="text-sm text-slate-500">오늘의 브리핑을 불러오는 중입니다.</div>
    }
    return (
        <div className="space-y-3">
            {briefing.counts ? (
                <div className="flex flex-wrap gap-1.5">
                    {Object.entries(briefing.counts).map(([key, count]) => (
                        <span key={key} className="rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700 ring-1 ring-slate-200">
                            {BRIEFING_CATEGORY_LABELS[key] || key} {count}
                        </span>
                    ))}
                </div>
            ) : null}
            {items.length === 0 ? (
                <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">
                    오늘은 별도 고객 업무 브리핑 항목이 없습니다. 일정은 아래 캘린더에서 확인해 주세요.
                </div>
            ) : (
                <div className="space-y-2">
                    {visibleItems.map((item, idx) => {
                        const high = item.severity === 'HIGH'
                        const isMaturity = item.category === 'MATURITY'
                        const dateText = item.nextMaturityDate || item.sortAt || ''
                        if (isMaturity) {
                            return (
                                <div key={`${item.category || 'item'}-${item.maturityPolicyId || idx}`} className={`flex flex-col gap-2 rounded-lg border px-3 py-2 text-xs transition sm:flex-row sm:items-center ${
                                    high ? 'border-rose-200 bg-rose-50/80' : 'border-slate-200 bg-white'
                                }`}>
                                    <Link to={item.targetPath || '/support/maturity-ledger'} className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                                        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                                high ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                                            }`}>
                                                {item.label || '만기관리'}
                                            </span>
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                                high ? 'bg-white text-rose-700 ring-1 ring-rose-200' : 'bg-white text-slate-600 ring-1 ring-slate-200'
                                            }`}>
                                                {high ? '중요' : '보통'}
                                            </span>
                                            {dateText ? (
                                                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200">
                                                    {dateText}
                                                </span>
                                            ) : null}
                                        </div>
                                        <div className="min-w-0 flex-1 truncate font-bold text-slate-900">{item.title || '-'}</div>
                                    </Link>
                                    <Link
                                        to={item.contactPath || item.targetPath || '/support/maturity-ledger'}
                                        className="shrink-0 rounded border border-brand-navy/20 bg-white px-2 py-1 text-[11px] font-bold text-brand-navy hover:bg-sky-50"
                                    >
                                        {item.ctaLabel || '연락하기'} →
                                    </Link>
                                </div>
                            )
                        }
                        const inner = (
                            <div className={`rounded-lg border p-3 transition ${
                                high ? 'border-rose-200 bg-rose-50/80 hover:bg-rose-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                            }`}>
                                <div className="flex items-center gap-1.5">
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                        high ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'
                                    }`}>
                                        {item.label || BRIEFING_CATEGORY_LABELS[item.category] || '업무'}
                                    </span>
                                    {high ? <span className="text-[10px] font-semibold text-rose-600">우선</span> : null}
                                </div>
                                <div className="mt-1 line-clamp-1 text-sm font-bold text-slate-900">{item.title || '-'}</div>
                                <div className="mt-1 line-clamp-1 text-xs text-slate-600">{item.message || ''}</div>
                            </div>
                        )
                        return item.targetPath ? (
                            <Link key={`${item.category || 'item'}-${item.leadId || item.calendarEventId || item.maturityPolicyId || idx}`} to={item.targetPath}>
                                {inner}
                            </Link>
                        ) : (
                            <div key={`${item.category || 'item'}-${idx}`}>{inner}</div>
                        )
                    })}
                    {(hiddenCount > 0 || hiddenMaturityCount > 0) ? (
                        <div className="flex flex-col gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                화면에는 우선순위 기준 {visibleLimit}건만 표시합니다.
                                {hiddenCount > 0 ? ` 숨겨진 업무 ${hiddenCount}건` : ''}
                                {hiddenMaturityCount > 0 ? `, 만기 ${hiddenMaturityCount}건` : ''}
                                은 상세 화면에서 확인하세요.
                            </div>
                            <div className="flex gap-2">
                                {hiddenMaturityCount > 0 ? (
                                    <Link to="/support/maturity-ledger" className="font-bold text-brand-navy hover:underline">
                                        만기대장 →
                                    </Link>
                                ) : null}
                                <Link to="/work/calendar" className="font-bold text-brand-navy hover:underline">
                                    일정관리 →
                                </Link>
                            </div>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    )
}

// 배너 이미지 컴포넌트 (blob 방식으로 이미지 로드)
function BannerImage({ imageFileId, title, targetUrl }) {
    const [imageUrl, setImageUrl] = useState(null)
    const [imageLoading, setImageLoading] = useState(false)

    useEffect(() => {
        if (!imageFileId || !String(imageFileId).trim()) {
            setImageUrl(null)
            setImageLoading(false)
            return
        }

        // 캐시에서 확인
        const cachedUrl = bannerImageCache.get(imageFileId)
        if (cachedUrl) {
            setImageUrl(cachedUrl)
            setImageLoading(false)
            return
        }

        // 이미 진행 중인 Promise가 있으면 재사용
        let promise = bannerImagePromises.get(imageFileId)
        if (!promise) {
            // 새로운 Promise 생성
            promise = (async () => {
                try {
                    const res = await api.get(`/files/${imageFileId}/preview`, { responseType: 'blob' })
                    const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
                    const url = URL.createObjectURL(blob)
                    // 캐시에 저장
                    bannerImageCache.set(imageFileId, url)
                    // Promise 캐시에서 제거 (완료되었으므로)
                    bannerImagePromises.delete(imageFileId)
                    return url
                } catch (err) {
                    console.error('Banner image load error:', { imageFileId, err })
                    // 에러 시 캐시에서 제거
                    bannerImageCache.delete(imageFileId)
                    bannerImagePromises.delete(imageFileId)
                    throw err
                }
            })()
            // Promise 캐시에 저장
            bannerImagePromises.set(imageFileId, promise)
        }

        // Promise가 완료될 때까지 대기
        let cancelled = false
        setImageLoading(true)
        promise.then(url => {
            if (!cancelled) {
                setImageUrl(prev => {
                    try { if (prev && prev !== url) URL.revokeObjectURL(prev) } catch { }
                    return url
                })
                setImageLoading(false)
            }
        }).catch(() => {
            if (!cancelled) {
                setImageUrl(null)
                setImageLoading(false)
            }
        })

        return () => {
            cancelled = true
        }
    }, [imageFileId])

    const content = imageUrl ? (
        <div className="flex h-full w-full items-center justify-center bg-slate-100">
            <img
                src={imageUrl}
                alt={title || '배너'}
                className="max-h-full max-w-full object-contain"
            />
        </div>
    ) : imageLoading ? (
        <div className="h-full w-full flex items-center justify-center bg-slate-50 text-slate-400">
            <span className="text-sm">로딩 중...</span>
        </div>
    ) : (
        <div className="h-full w-full flex items-center justify-center bg-slate-100 text-slate-400">
            <span className="text-sm">이미지 없음</span>
        </div>
    )

    if (targetUrl) {
        return (
            <a
                href={targetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full w-full"
            >
                {content}
            </a>
        )
    }

    return <div className="h-full w-full">{content}</div>
}

function DashboardSection({ title, action, collapsed, onToggle, children, className = '' }) {
    return (
        <div className={`rounded-lg border bg-white ${className}`}>
            <div className="flex items-start justify-between gap-3 px-4 py-3">
                <button
                    type="button"
                    className="min-w-0 flex-1 text-left"
                    onClick={onToggle}
                    aria-expanded={!collapsed}
                >
                    <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-slate-900">{title}</span>
                        <span className="text-xs font-medium text-slate-400">{collapsed ? '펼치기' : '접기'}</span>
                    </div>
                </button>
                {action ? <div className="shrink-0">{action}</div> : null}
            </div>
            {!collapsed ? (
                <div className="border-t border-slate-100 px-4 py-4">
                    {children}
                </div>
            ) : null}
        </div>
    )
}

function unwrapApiData(res) {
    return (res?.data && res.data.data !== undefined) ? res.data.data : res?.data
}

function firstNonBlank(...values) {
    for (const value of values) {
        const text = value == null ? '' : String(value).trim()
        if (text) return text
    }
    return ''
}

const MONTHLY_CARD_NEWS_KIND_LABELS = {
    life: '생보',
    fire: '손보',
    insurerLife: '생보 이미지',
    insurerFire: '손보 이미지',
}

const MONTHLY_CARD_NEWS_KIND_CLASSES = {
    life: 'bg-sky-100 text-sky-800 ring-sky-200',
    fire: 'bg-violet-100 text-violet-800 ring-violet-200',
    insurerLife: 'bg-cyan-100 text-cyan-800 ring-cyan-200',
    insurerFire: 'bg-fuchsia-100 text-fuchsia-800 ring-fuchsia-200',
}

function buildMonthlyCardNewsSlides(brief, images, insurerImages = []) {
    const payload = brief?.payload || null
    const build = (kind) => sortMonthlyBriefImagesBySlideOrder(
        filterMonthlyBriefImagesByKind(images, kind),
        payload,
        kind,
    ).map((img, idx) => {
        const storageSlideId = parseStorageSlideIdFromMonthlyBriefImageName(img?.imageName)
        const meta = findSlideMetaForMonthlyBriefImage(payload, kind, storageSlideId)
        const kindLabel = MONTHLY_CARD_NEWS_KIND_LABELS[kind] || '카드뉴스'
        return {
            id: `${kind}-${img?.id || img?.imageName || idx}`,
            kind,
            kindLabel,
            fileId: img?.fileId || '',
            imageName: img?.imageName || '',
            title: meta?.title || `${kindLabel} 카드뉴스 ${idx + 1}`,
            body: meta?.body || '',
        }
    })
    const monthlySlides = [...build('life'), ...build('fire')]
    const insurerSlides = (Array.isArray(insurerImages) ? insurerImages : []).map((img, idx) => {
        const type = String(img?.type || '').toUpperCase()
        const kind = type === 'FIRE' ? 'insurerFire' : 'insurerLife'
        const kindLabel = MONTHLY_CARD_NEWS_KIND_LABELS[kind] || '보험사 이미지'
        const insurerName = firstNonBlank(img?.insurerName)
        return {
            id: `${kind}-${img?.newsletterId || img?.imageId || img?.id || img?.fileId || idx}`,
            kind,
            kindLabel,
            fileId: img?.fileId || '',
            imageName: img?.imageName || '',
            title: insurerName ? `${insurerName} 이미지` : `${kindLabel} ${idx + 1}`,
            body: firstNonBlank(img?.summary),
        }
    })
    return [...monthlySlides, ...insurerSlides].filter((slide) => slide.fileId)
}

function getInsurerNewsletterDashboardSummary(item) {
    return firstNonBlank(
        item?.summaryManual,
        item?.summaryArbitrated,
        item?.summaryGpt4oMini,
        item?.summaryGemini,
    )
}

async function fetchDashboardInsurerGeneratedImages(year, month, type) {
    let items = []
    try {
        const listRes = await api.get('/insurer-newsletters', {
            params: { year, month, type },
            __silent: true,
        })
        const payload = unwrapApiData(listRes)
        items = Array.isArray(payload) ? payload : (payload?.items || [])
    } catch {
        return []
    }
    const results = await Promise.all((Array.isArray(items) ? items : []).map(async (item) => {
        if (!item?.id) return null
        try {
            const imageRes = await api.get(`/insurer-newsletters/${item.id}/generated-image`, { __silent: true })
            const image = unwrapApiData(imageRes)
            if (!image?.exists || !image?.fileId) return null
            return {
                ...image,
                newsletterId: item.id,
                insurerName: item.insurerName || '',
                type,
                year: item.year || year,
                month: item.month || month,
                summary: getInsurerNewsletterDashboardSummary(item),
            }
        } catch {
            return null
        }
    }))
    return results.filter(Boolean)
}

function DashboardCardNewsSummaryModal({ slide, onClose, onPrev, onNext, canPrev, canNext }) {
    useEffect(() => {
        if (!slide) return undefined
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose()
            if (event.key === 'ArrowLeft' && canPrev) onPrev()
            if (event.key === 'ArrowRight' && canNext) onNext()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [canNext, canPrev, onClose, onNext, onPrev, slide])

    if (!slide) return null

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
            <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
                <div className="flex items-start justify-between gap-3 border-b bg-slate-50 px-4 py-3 sm:px-5">
                    <div className="min-w-0">
                        <div className={`mb-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${MONTHLY_CARD_NEWS_KIND_CLASSES[slide.kind] || 'bg-slate-100 text-slate-700 ring-slate-200'}`}>
                            {slide.kindLabel}
                        </div>
                        <h3 className="line-clamp-2 text-base font-bold text-slate-900 sm:text-lg">{slide.title}</h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy"
                        aria-label="닫기"
                    >
                        <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>
                <div className="grid min-h-0 flex-1 grid-cols-1 overflow-auto lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
                    <div className="flex min-h-[280px] items-center justify-center bg-slate-100 p-3 sm:p-4">
                        <img
                            src={resolveApiAssetUrl(slide.fileId)}
                            alt={slide.title}
                            className="max-h-[64vh] max-w-full rounded-lg border border-slate-200 bg-white object-contain shadow-sm"
                        />
                    </div>
                    <aside className="border-t border-slate-200 bg-white p-4 lg:border-l lg:border-t-0">
                        <h4 className="text-sm font-bold text-slate-900">요약</h4>
                        <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                            {slide.body || '요약이 등록되지 않았습니다.'}
                        </div>
                    </aside>
                </div>
                <div className="flex items-center justify-between gap-3 border-t bg-white px-4 py-3">
                    <button
                        type="button"
                        onClick={onPrev}
                        disabled={!canPrev}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                        이전
                    </button>
                    <button
                        type="button"
                        onClick={onNext}
                        disabled={!canNext}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        다음
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </div>
    )
}

function DashboardMonthlyCardNews({ query, periodLabel }) {
    const scrollerRef = useRef(null)
    const [paused, setPaused] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(null)
    const cardNews = query.data || {}
    const slides = useMemo(
        () => buildMonthlyCardNewsSlides(
            cardNews.brief,
            Array.isArray(cardNews.images) ? cardNews.images : [],
            Array.isArray(cardNews.insurerImages) ? cardNews.insurerImages : [],
        ),
        [cardNews.brief, cardNews.images, cardNews.insurerImages],
    )
    const selectedSlide = selectedIndex == null ? null : slides[selectedIndex]

    useEffect(() => {
        if (paused || selectedIndex != null || slides.length <= 1) return undefined
        if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined

        const interval = setInterval(() => {
            const el = scrollerRef.current
            if (!el) return
            const maxLeft = el.scrollWidth - el.clientWidth
            if (maxLeft <= 4) return
            const firstItem = el.querySelector('[data-card-news-item="true"]')
            const step = firstItem ? firstItem.getBoundingClientRect().width + 12 : 180
            if (el.scrollLeft >= maxLeft - 8) {
                el.scrollTo({ left: 0, behavior: 'smooth' })
            } else {
                el.scrollBy({ left: step, behavior: 'smooth' })
            }
        }, 3200)
        return () => clearInterval(interval)
    }, [paused, selectedIndex, slides.length])

    useEffect(() => {
        if (selectedIndex != null && selectedIndex >= slides.length) {
            setSelectedIndex(slides.length > 0 ? slides.length - 1 : null)
        }
    }, [selectedIndex, slides.length])

    const goSummary = useCallback((nextIndex) => {
        if (slides.length === 0) return
        setSelectedIndex(Math.max(0, Math.min(slides.length - 1, nextIndex)))
    }, [slides.length])

    if (query.isLoading) {
        return (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[0, 1, 2].map((idx) => (
                    <div key={idx} className="aspect-[4/5] animate-pulse rounded-lg bg-slate-100" />
                ))}
            </div>
        )
    }

    if (query.isError) {
        return <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">카드뉴스를 불러오지 못했습니다.</div>
    }

    if (slides.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
                {periodLabel} 카드뉴스가 없습니다.
            </div>
        )
    }

    return (
        <>
            <div
                ref={scrollerRef}
                className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory [scrollbar-width:thin]"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onFocus={() => setPaused(true)}
                onBlur={() => setPaused(false)}
            >
                {slides.map((slide, index) => (
                    <button
                        key={slide.id}
                        type="button"
                        data-card-news-item="true"
                        className="group w-[min(44vw,156px)] shrink-0 snap-start overflow-hidden rounded-lg border border-slate-200 bg-white text-left shadow-sm outline-none transition hover:-translate-y-0.5 hover:shadow-md focus-visible:ring-2 focus-visible:ring-brand-navy sm:w-[150px]"
                        onClick={() => setSelectedIndex(index)}
                        aria-label={`${slide.kindLabel} 카드뉴스 요약 보기: ${slide.title}`}
                    >
                        <div className="relative aspect-[4/5] bg-slate-100">
                            <img
                                src={resolveApiAssetUrl(slide.fileId)}
                                alt=""
                                className="h-full w-full object-cover object-center"
                                loading="lazy"
                                decoding="async"
                            />
                            <span className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${MONTHLY_CARD_NEWS_KIND_CLASSES[slide.kind] || 'bg-slate-100 text-slate-700 ring-slate-200'}`}>
                                {slide.kindLabel}
                            </span>
                        </div>
                        <div className="px-3 py-2">
                            <div className="line-clamp-2 min-h-[2.25rem] text-xs font-semibold leading-[1.15rem] text-slate-900">
                                {slide.title}
                            </div>
                        </div>
                    </button>
                ))}
            </div>
            <DashboardCardNewsSummaryModal
                slide={selectedSlide}
                onClose={() => setSelectedIndex(null)}
                onPrev={() => goSummary((selectedIndex || 0) - 1)}
                onNext={() => goSummary((selectedIndex || 0) + 1)}
                canPrev={selectedIndex != null && selectedIndex > 0}
                canNext={selectedIndex != null && selectedIndex < slides.length - 1}
            />
        </>
    )
}

// 즐겨찾기 메뉴 섹션 컴포넌트
function FavoriteMenuSection({ embedded = false, toolbarOnly = false } = {}) {
    const { hasAnyRole, hasAnyPermission, hasAnyCapability, user } = useAuth()
    const [favorites, setFavorites] = useState(() => loadFavorites())
    const [showEditModal, setShowEditModal] = useState(false)
    const [tempFavorites, setTempFavorites] = useState(new Set(favorites))

    const menuCtx = useMemo(
        () => createMenuVisibilityContext(user, { hasAnyRole, hasAnyPermission, hasAnyCapability }),
        [user, hasAnyRole, hasAnyPermission, hasAnyCapability],
    )

    const visibleMenuItems = useMemo(() => {
        return filterVisibleMenuItems(MENU_ITEMS, menuCtx, { surface: MENU_SURFACES.WEB_DASHBOARD })
    }, [menuCtx])

    // 카테고리별 메뉴 그룹화
    const menuByCategory = useMemo(() => {
        const grouped = {}
        visibleMenuItems.forEach(item => {
            if (!grouped[item.category]) {
                grouped[item.category] = []
            }
            grouped[item.category].push(item)
        })
        return grouped
    }, [visibleMenuItems])

    // 즐겨찾기된 메뉴만 필터링
    const favoriteMenuItems = useMemo(() => {
        return visibleMenuItems.filter(item => favorites.has(item.id))
    }, [visibleMenuItems, favorites])

    // 즐겨찾기 토글
    const toggleFavorite = useCallback((menuId) => {
        setTempFavorites(prev => {
            const next = new Set(prev)
            if (next.has(menuId)) {
                next.delete(menuId)
            } else {
                next.add(menuId)
            }
            return next
        })
    }, [])

    /** 해당 대메뉴(카테고리) 안의 메뉴만 즐겨찾기에 일괄 추가/제거 */
    const setCategoryFavorites = useCallback((category, selectAll) => {
        const items = menuByCategory[category] || []
        const ids = items.map((it) => it.id)
        setTempFavorites((prev) => {
            const next = new Set(prev)
            for (const id of ids) {
                if (selectAll) next.add(id)
                else next.delete(id)
            }
            return next
        })
    }, [menuByCategory])

    // 즐겨찾기 저장
    const handleSaveFavorites = useCallback(() => {
        setFavorites(tempFavorites)
        saveFavorites(tempFavorites)
        setShowEditModal(false)
    }, [tempFavorites])

    // 수정 모달 열기
    const handleOpenEdit = useCallback(() => {
        setTempFavorites(new Set(favorites))
        setShowEditModal(true)
    }, [favorites])

    // 수정 모달 닫기
    const handleCloseEdit = useCallback(() => {
        setTempFavorites(new Set(favorites))
        setShowEditModal(false)
    }, [favorites])

    // 즐겨찾기 변경 시 localStorage 저장
    useEffect(() => {
        saveFavorites(favorites)
    }, [favorites])

    if (toolbarOnly) {
        return (
            <>
                {/* <button
                    type="button"
                    onClick={handleOpenEdit}
                    className="text-sm text-slate-600 hover:text-brand-sky hover:underline whitespace-nowrap"
                >
                    즐겨찾기 편집
                </button> */}
                {showEditModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleCloseEdit}>
                        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between p-4 border-b">
                                <h3 className="text-lg font-semibold">즐겨찾기 메뉴 선택</h3>
                                <button
                                    type="button"
                                    onClick={handleCloseEdit}
                                    className="text-slate-400 hover:text-slate-600"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                                        <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4">
                                {Object.entries(menuByCategory).map(([category, items]) => (
                                    <div key={category} className="mb-6">
                                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                            <h4 className="text-sm font-semibold text-slate-700">{MENU_CATEGORIES[category] || category}</h4>
                                            <div className="flex gap-1.5">
                                                <button
                                                    type="button"
                                                    className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                                                    onClick={() => setCategoryFavorites(category, true)}
                                                >
                                                    전체 선택
                                                </button>
                                                <button
                                                    type="button"
                                                    className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                                                    onClick={() => setCategoryFavorites(category, false)}
                                                >
                                                    전체 해제
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            {items.map((item) => (
                                                <label
                                                    key={item.id}
                                                    className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={tempFavorites.has(item.id)}
                                                        onChange={() => toggleFavorite(item.id)}
                                                        className="rounded border-slate-300 text-brand-sky focus:ring-brand-sky"
                                                    />
                                                    <span className="text-lg mr-2">{item.icon}</span>
                                                    <span className="text-sm flex-1">{item.label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center justify-end gap-2 p-4 border-t">
                                <button
                                    type="button"
                                    onClick={handleCloseEdit}
                                    className="px-4 py-2 text-sm rounded border hover:bg-slate-50"
                                >
                                    취소
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveFavorites}
                                    className="px-4 py-2 text-sm rounded bg-brand-sky text-white hover:bg-brand-sky/90"
                                >
                                    저장
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        )
    }

    return (
        <>
            <div className={embedded ? '' : 'rounded-lg border bg-white p-4'}>
                {!embedded && (
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold">즐겨찾기</h2>
                        <button
                            type="button"
                            onClick={handleOpenEdit}
                            className="text-sm text-brand-sky hover:underline"
                        >
                            수정
                        </button>
                    </div>
                )}
                {embedded && favoriteMenuItems.length > 0 && (
                    <div className="mb-3 flex justify-end">
                        <button
                            type="button"
                            onClick={handleOpenEdit}
                            className="text-sm text-brand-sky hover:underline"
                        >
                            수정
                        </button>
                    </div>
                )}
                {favoriteMenuItems.length === 0 ? (
                    <div className="text-sm text-slate-500 text-center py-8">
                        즐겨찾기된 메뉴가 없습니다.
                        <br />
                        <button
                            type="button"
                            onClick={handleOpenEdit}
                            className="text-brand-sky hover:underline mt-2"
                        >
                            즐겨찾기 추가하기
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3">
                        {favoriteMenuItems.map((item) => (
                            <Link
                                key={item.id}
                                to={item.path}
                                className="flex flex-col items-center justify-center rounded-lg border p-2 md:p-4 hover:bg-slate-50 transition"
                            >
                                <span className="text-base md:text-2xl mb-1 md:mb-2">{item.icon}</span>
                                <span className="text-[10px] md:text-sm font-medium text-center leading-tight">{item.label}</span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>

            {/* 즐겨찾기 수정 모달 */}
            {showEditModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleCloseEdit}>
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-lg font-semibold">즐겨찾기 메뉴 선택</h3>
                            <button
                                type="button"
                                onClick={handleCloseEdit}
                                className="text-slate-400 hover:text-slate-600"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                                    <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {Object.entries(menuByCategory).map(([category, items]) => (
                                <div key={category} className="mb-6">
                                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                        <h4 className="text-sm font-semibold text-slate-700">{MENU_CATEGORIES[category] || category}</h4>
                                        <div className="flex gap-1.5">
                                            <button
                                                type="button"
                                                className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                                                onClick={() => setCategoryFavorites(category, true)}
                                            >
                                                전체 선택
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
                                                onClick={() => setCategoryFavorites(category, false)}
                                            >
                                                전체 해제
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {items.map((item) => (
                                            <label
                                                key={item.id}
                                                className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={tempFavorites.has(item.id)}
                                                    onChange={() => toggleFavorite(item.id)}
                                                    className="rounded border-slate-300 text-brand-sky focus:ring-brand-sky"
                                                />
                                                <span className="text-lg mr-2">{item.icon}</span>
                                                <span className="text-sm flex-1">{item.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-end gap-2 p-4 border-t">
                            <button
                                type="button"
                                onClick={handleCloseEdit}
                                className="px-4 py-2 text-sm rounded border hover:bg-slate-50"
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveFavorites}
                                className="px-4 py-2 text-sm rounded bg-brand-sky text-white hover:bg-brand-sky/90"
                            >
                                저장
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default function DashboardPage() {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const briefingSectionRef = useRef(null)
    const { hasAnyRole, hasInsuranceNewsAccess } = useAuth()
    const { notifications, dismiss } = useNotifications()

    // 역할 그룹
    // GA_MASTER_ADMIN, GA_MASTER_DEPUTY_ADMIN도 GA 관리자급 역할로 포함
    const isOperator = hasAnyRole([Roles.OPERATOR])
    const canUseInsurerNewsletters = Boolean(hasInsuranceNewsAccess && hasInsuranceNewsAccess())
    const isGaAdmin = hasAnyRole([Roles.GA_LEADER, Roles.GA_MANAGER, Roles.GA_MASTER_ADMIN, Roles.GA_MASTER_DEPUTY_ADMIN])
    const isInsAdmin = hasAnyRole([Roles.INSURER_LEADER, Roles.INSURER_BRANCH_MANAGER, Roles.INSURER_DEPUTY_BRANCH_MANAGER])
    const isGaPlanner = hasAnyRole([Roles.GA_PLANNER])
    const isInsManager = hasAnyRole([Roles.INSURER_MANAGER, Roles.INSURER_EDUCATION_MANAGER, Roles.INSURER_PLAN_MANAGER])
    const isGaUser = hasAnyRole([Roles.GA_LEADER, Roles.GA_MANAGER, Roles.GA_PLANNER, Roles.GA_MASTER_ADMIN, Roles.GA_MASTER_DEPUTY_ADMIN])

    const isAdminView = isOperator || isGaAdmin || isInsAdmin
    const isGaPlannerView = !isAdminView && isGaPlanner
    const isInsManagerView = !isAdminView && !isGaPlannerView && isInsManager

    const [kpi, setKpi] = useState({
        new: '-',
        fix: '-',
        done: '-',
        avgDays: '-',
        total: '-',
        pending: '-',
        inProgress: '-',
        completed: '-',
        completionRate: '-'
    })
    const [series, setSeries] = useState([])
    /** 상세 대시보드(비노출) 레거시 UI용 — 실제로는 `false` 가드로 렌더되지 않음 */
    const [orgId, setOrgId] = useState('')
    const [orgOptions] = useState([])
    const [, setLoading] = useState(false)
    const [collapsedSections, setCollapsedSections] = useState({
        alerts: false,
        briefing: false,
        calendar: false,
        news: false,
    })

    const toggleSection = useCallback((sectionKey) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [sectionKey]: !prev[sectionKey],
        }))
    }, [])

    // 공지: 분류별(우리조직/원수사) 카운트
    const [noticeInternalUnreadCount, setNoticeInternalUnreadCount] = useState(0)
    const [noticeInsurerUnreadCount, setNoticeInsurerUnreadCount] = useState(0)
    const [noticeApprovalNeededCount, setNoticeApprovalNeededCount] = useState(0)
    const canApproveNotice = hasAnyRole([
        Roles.OPERATOR,
        Roles.GA_LEADER,
        Roles.GA_MANAGER,
        Roles.GA_MASTER_ADMIN,
        Roles.GA_MASTER_DEPUTY_ADMIN,
        Roles.INSURER_MASTER_ADMIN,
        Roles.INSURER_MASTER_DEPUTY_ADMIN,
        Roles.INSURER_LEADER,
        Roles.INSURER_DEPUTY_LEADER,
        Roles.INSURER_OTHER_ADMIN,
        Roles.INSURER_BRANCH_MANAGER,
        Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    ])
    const myNoticeOrgType = isGaAdmin || isGaPlannerView ? 'GA' : (isInsAdmin || isInsManagerView ? 'INSURER' : '')

    // 데이터 로딩 함수
    const loadDashboardData = useCallback(async () => {
        setLoading(true)
        try {
            const params = { periodType: 'week' } // 대시보드는 항상 "현재 주간" 기준

            const [kpiRes, seriesRes] = await Promise.all([
                api.get('/dashboard/kpi', { params }),
                api.get('/dashboard/timeseries', { params }),
            ])

            const kpiData = kpiRes.data?.data || {}
            setKpi({
                new: kpiData.newCount ?? '-',
                fix: kpiData.fixCount ?? '-',
                done: kpiData.doneCount ?? '-',
                avgDays: kpiData.avgLeadDays ?? '-',
                total: kpiData.totalRequests ?? '-',
                pending: kpiData.pendingCount ?? '-',
                inProgress: kpiData.inProgressCount ?? '-',
                completed: kpiData.completedCount ?? '-',
                completionRate: kpiData.completionRate ?? '-'
            })

            setSeries(Array.isArray(seriesRes.data?.data) ? seriesRes.data.data : [])
        } catch (error) {
            console.error('Failed to load dashboard data:', error)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadDashboardData()
    }, [loadDashboardData])

    // 공지 카운트 로딩(대시보드 카드)
    useEffect(() => {
        let ignore = false
        async function loadNoticeCounts() {
            try {
                const res = await api.get('/notices/dashboard-counts')
                const payload = res?.data?.data ?? res?.data ?? {}
                const internalUnread = payload.internalUnreadCount ?? 0
                const insurerUnread = payload.insurerUnreadCount ?? 0
                const appr = payload.approvalNeededCount ?? 0
                if (!ignore) {
                    setNoticeInternalUnreadCount(Number(internalUnread) || 0)
                    setNoticeInsurerUnreadCount(Number(insurerUnread) || 0)
                    setNoticeApprovalNeededCount(Number(appr) || 0)
                }
            } catch {
                if (!ignore) {
                    setNoticeInternalUnreadCount(0)
                    setNoticeInsurerUnreadCount(0)
                    setNoticeApprovalNeededCount(0)
                }
            }
        }
        loadNoticeCounts()
        return () => { ignore = true }
    }, [canApproveNotice])

    // SSE 기반 웹알림(실시간 알림) → NotificationCenter에서 가져와 대시보드 알림으로 표시
    const alerts = useMemo(() => {
        if (!Array.isArray(notifications) || notifications.length === 0) return []
        return notifications.map((n, idx) => {
            const payload = n.payload || {}
            const type = payload.type || (payload.test ? 'test' : 'info')
            let title = payload.title || '알림'
            let message = ''
            let targetPath = null

            if (payload.type === 'SIGNUP_REQUEST_CREATED') {
                title = '가입 신청 접수'
                message = `${payload.orgName || ''}에 ${payload.name || payload.email || ''} 님의 가입 신청이 접수되었습니다.`.trim()
                if (payload.signupId) {
                    targetPath = `/signups/${payload.signupId}`
                } else {
                    targetPath = '/signups'
                }
            } else if (payload.type === 'WITHDRAWAL_REQUEST_CREATED') {
                title = payload.title || '탈퇴 승인 요청'
                message = (payload.message || payload.summary || '').trim()
                    || `${payload.name || payload.email || ''} 님의 탈퇴 승인 요청이 접수되었습니다.`.trim()
                targetPath = '/withdrawal-approvals'
            } else if (payload.type === 'SIGNUP_APPROVED') {
                title = '가입 신청 승인'
                message = '가입 신청이 승인되었습니다.'
                targetPath = '/profile'
            } else if (payload.type === 'SIGNUP_REJECTED') {
                title = '가입 신청 반려'
                message = '가입 신청이 반려되었습니다.'
                targetPath = '/profile'
            } else if (payload.insurerOrgName || payload.requestId) {
                // 의뢰 생성/배정 계열
                title = payload.title || '의뢰 알림'
                if (payload.insurerOrgName) {
                    message = `${payload.insurerOrgName}에 의뢰가 배정되었습니다.`
                } else if (payload.requestId) {
                    message = `의뢰 #${payload.requestId} 관련 알림입니다.`
                }
                if (payload.requestId !== null && payload.requestId !== undefined && payload.requestId !== 0) {
                    targetPath = `/requests/${payload.requestId}`
                } else {
                    // 테스트 알림 등 requestId가 유효하지 않은 경우 목록으로 이동
                    targetPath = '/requests'
                }
            } else if (payload.type === 'NOTICE_APPROVAL_NEEDED' && payload.noticeId) {
                title = '공지 승인 필요'
                message = payload.title ? `승인 필요: ${payload.title}` : '승인이 필요한 공지가 있습니다.'
                targetPath = `/notices/${payload.noticeId}`
            } else if (payload.type === 'NOTICE_APPROVED' && payload.noticeId) {
                title = '새 공지'
                message = payload.title ? `새 공지: ${payload.title}` : '새 공지가 등록되었습니다.'
                targetPath = `/notices/${payload.noticeId}`
            } else if (payload.type === 'LEAD_CREATED' && payload.leadId) {
                title = payload.title || '상담신청'
                message = payload.message || payload.summary || '새 상담신청이 도착했습니다.'
                targetPath = payload.code ? `/sales/consultations?leadId=${encodeURIComponent(payload.leadId)}` : '/sales/consultations'
            } else if (payload.type === 'CALENDAR') {
                title = payload.title || '일정 알림'
                const sum = String(payload.summary || '').trim()
                const action = String(payload.action || '').trim()
                if (action === 'REMINDER') {
                    const offRaw = payload.offsetMinutes
                    const offNum = offRaw != null && offRaw !== '' ? Number(offRaw) : null
                    const offLabel =
                        offNum != null && Number.isFinite(offNum) && offNum > 0 ? `시작 ${offNum}분 전` : ''
                    message = [sum, offLabel].filter(Boolean).join(' · ') || '일정 시작 알림입니다.'
                } else {
                    message = sum || '일정 관련 알림이 있습니다.'
                }
                targetPath = '/work/calendar'
            } else {
                const human = String(payload.summary || payload.message || payload.body || '').trim()
                message = human || (Object.keys(payload).length > 0 ? JSON.stringify(payload) : '')
            }

            return {
                id: n.id || `${n.receivedAt}-${idx}`,
                type: type === 'warning' || type === 'error' || type === 'info' ? type : 'info',
                title,
                message,
                details: payload.test ? '테스트 알림입니다.' : null,
                createdAt: n.receivedAt,
                targetPath,
            }
        })
    }, [notifications])

    // 알림 페이징 (최신순, 페이지당 5개)
    const [alertPage, setAlertPage] = useState(0)
    const pageSize = 5
    const totalPages = alerts.length > 0 ? Math.ceil(alerts.length / pageSize) : 0
    const pagedAlerts = alerts.slice(alertPage * pageSize, alertPage * pageSize + pageSize)
    // 파이 차트 데이터 준비
    const pieData = [
        { name: '대기', value: Number(kpi.pending) || 0, color: '#F59E0B' },
        { name: '진행중', value: Number(kpi.inProgress) || 0, color: '#38BDF8' },
        { name: '완료', value: Number(kpi.completed) || 0, color: '#2DD4BF' },
        { name: '보완', value: Number(kpi.fix) || 0, color: '#EF4444' }
    ].filter(item => item.value > 0)

    // 플랫폼 감지 (PC WEB / Mobile WEB)
    const [platform, setPlatform] = useState('WEB_PC')
    useEffect(() => {
        const updatePlatform = () => {
            const width = window.innerWidth
            setPlatform(width >= 768 ? 'WEB_PC' : 'WEB_MOBILE')
        }
        updatePlatform()
        window.addEventListener('resize', updatePlatform)
        return () => window.removeEventListener('resize', updatePlatform)
    }, [])

    // 배너 이미지 조회 (PC/WEB용)
    const bannersQuery = useQuery({
        queryKey: ['banners-active', platform],
        queryFn: async () => {
            const res = await api.get('/banners/active', { params: { platform } })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const raw = Array.isArray(payload) ? payload : []
            return raw.map((banner) => ({
                ...banner,
                imageFileId: banner.imageFileId || banner.imageFileIdPc || banner.imageFileIdMobile || banner.imageFileIdApp || '',
            }))
        },
        staleTime: 5 * 60 * 1000, // 5분간 캐시
    })

    const DASHBOARD_HOME_LIST_LIMIT = 4
    const systemNoticesQuery = useQuery({
        queryKey: ['system-notices-home-all', DASHBOARD_HOME_LIST_LIMIT],
        queryFn: async () => {
            const res = await api.get('/system-notices', {
                params: {
                    page: 0,
                    size: DASHBOARD_HOME_LIST_LIMIT,
                },
            })
            const payload = unwrapApiData(res)
            const raw = Array.isArray(payload) ? payload : (payload?.content || payload?.items || [])
            return Array.isArray(raw) ? raw : []
        },
        staleTime: 2 * 60 * 1000,
        refetchOnMount: 'always',
    })

    const dailyBriefingQuery = useQuery({
        queryKey: ['daily-work-briefing', 'today'],
        queryFn: async () => {
            const res = await api.get('/daily-work-briefings/today')
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            return payload && typeof payload === 'object' ? payload : null
        },
        staleTime: 60 * 1000,
        refetchOnMount: 'always',
    })

    useEffect(() => {
        if (searchParams.get('focus') !== 'briefing') return
        const next = new URLSearchParams(searchParams)
        next.delete('focus')
        setSearchParams(next, { replace: true })
        requestAnimationFrame(() => {
            briefingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
    }, [searchParams, setSearchParams])

    const currentMonth = useMemo(() => {
        const now = new Date()
        return { year: now.getFullYear(), month: now.getMonth() + 1 }
    }, [])

    const monthlyCardNewsQuery = useQuery({
        queryKey: ['dashboard-monthly-card-news', currentMonth.year, currentMonth.month],
        queryFn: async () => {
            let brief = null
            let images = []
            try {
                const briefRes = await api.get('/insurer-newsletter-monthly-briefs', {
                    params: { year: currentMonth.year, month: currentMonth.month },
                    __silent: true,
                })
                brief = unwrapApiData(briefRes)
                if (brief?.id) {
                    const imagesRes = await api.get(`/insurer-newsletter-monthly-briefs/${brief.id}/card-images`, {
                        __silent: true,
                    })
                    const rawImages = unwrapApiData(imagesRes)
                    images = Array.isArray(rawImages) ? rawImages.filter((item) => item?.fileId) : []
                }
            } catch {
                brief = null
                images = []
            }

            const [lifeInsurerImages, fireInsurerImages] = await Promise.all([
                fetchDashboardInsurerGeneratedImages(currentMonth.year, currentMonth.month, 'LIFE'),
                fetchDashboardInsurerGeneratedImages(currentMonth.year, currentMonth.month, 'FIRE'),
            ])
            return {
                brief,
                images,
                insurerImages: [...lifeInsurerImages, ...fireInsurerImages],
            }
        },
        enabled: canUseInsurerNewsletters,
        staleTime: 5 * 60 * 1000,
    })

    const banners = bannersQuery.data || []
    const systemNotices = systemNoticesQuery.data || []
    const monthlyCardNewsPeriodLabel = monthlyCardNewsQuery.data?.brief?.year && monthlyCardNewsQuery.data?.brief?.month
        ? `${monthlyCardNewsQuery.data.brief.year}.${String(monthlyCardNewsQuery.data.brief.month).padStart(2, '0')}`
        : `${currentMonth.year}.${String(currentMonth.month).padStart(2, '0')}`

    // 배너 슬라이더 상태
    const [bannerIndex, setBannerIndex] = useState(0)
    const [bannerAutoPlay, setBannerAutoPlay] = useState(true)

    // 배너 자동 슬라이드
    useEffect(() => {
        if (!bannerAutoPlay || banners.length <= 1) return
        const interval = setInterval(() => {
            setBannerIndex((prev) => (prev + 1) % banners.length)
        }, 5000) // 5초마다 자동 슬라이드
        return () => clearInterval(interval)
    }, [bannerAutoPlay, banners.length])

    return (
        <section className="space-y-6">
            {/* 1. 배너 이미지 슬라이더 */}
            {bannersQuery.isLoading ? (
                <div className="h-24 rounded-lg border bg-slate-100 md:h-32" />
            ) : (
                <div>
                    <div className="relative rounded-lg border overflow-hidden bg-slate-100">
                        <div className="relative h-24 md:h-32">
                            {banners.length > 0 ? (
                                banners.map((banner, index) => (
                                    <div
                                        key={banner.id}
                                        className={`absolute inset-0 transition-opacity duration-500 ${
                                            index === bannerIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                                        }`}
                                        onMouseEnter={() => setBannerAutoPlay(false)}
                                        onMouseLeave={() => setBannerAutoPlay(true)}
                                    >
                                        <BannerImage
                                            imageFileId={banner.imageFileId}
                                            title={banner.title}
                                            targetUrl={banner.targetUrl}
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="flex h-full items-center justify-center px-4 text-center text-sm text-slate-400">
                                    현재 노출할 활성 배너가 없습니다.
                                </div>
                            )}
                        </div>
                        {/* 좌우 네비게이션 버튼 */}
                        {banners.length > 1 && (
                            <>
                                <button
                                    type="button"
                                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 rounded-full bg-white/80 hover:bg-white p-2 shadow-md transition"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setBannerIndex((prev) => (prev - 1 + banners.length) % banners.length)
                                        setBannerAutoPlay(false)
                                        setTimeout(() => setBannerAutoPlay(true), 10000)
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-slate-700">
                                        <path fillRule="evenodd" d="M11.03 3.97a.75.75 0 0 1 0 1.06l-6.22 6.22H21a.75.75 0 0 1 0 1.5H4.81l6.22 6.22a.75.75 0 1 1-1.06 1.06l-7.5-7.5a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                <button
                                    type="button"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 rounded-full bg-white/80 hover:bg-white p-2 shadow-md transition"
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setBannerIndex((prev) => (prev + 1) % banners.length)
                                        setBannerAutoPlay(false)
                                        setTimeout(() => setBannerAutoPlay(true), 10000)
                                    }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-slate-700">
                                        <path fillRule="evenodd" d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </>
                        )}
                        {/* 인디케이터 */}
                        {banners.length > 1 && (
                            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1">
                                {banners.map((_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`h-2 rounded-full transition-all ${
                                            index === bannerIndex ? 'w-6 bg-white' : 'w-2 bg-white/50'
                                        }`}
                                        onClick={() => {
                                            setBannerIndex(index)
                                            setBannerAutoPlay(false)
                                            setTimeout(() => setBannerAutoPlay(true), 10000)
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 2. 알림 섹션 (알림이 있을 때만 표시) */}
            {alerts.length > 0 && (
                <DashboardSection
                    title="알림"
                    collapsed={collapsedSections.alerts}
                    onToggle={() => toggleSection('alerts')}
                    action={totalPages > 1 ? (
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                            <button
                                type="button"
                                className="px-1.5 py-0.5 rounded border disabled:opacity-40 text-xs"
                                disabled={alertPage === 0}
                                onClick={() => setAlertPage((p) => Math.max(0, p - 1))}
                            >
                                이전
                            </button>
                            <span className="text-xs">{alertPage + 1} / {totalPages}</span>
                            <button
                                type="button"
                                className="px-1.5 py-0.5 rounded border disabled:opacity-40 text-xs"
                                disabled={alertPage >= totalPages - 1}
                                onClick={() => setAlertPage((p) => Math.min(totalPages - 1, p + 1))}
                            >
                                다음
                            </button>
                        </div>
                    ) : null}
                >
                    <div className="space-y-1.5">
                        {pagedAlerts.map(alert => (
                            <div
                                key={alert.id}
                                className={`relative rounded border p-2 cursor-pointer transition hover:shadow-sm ${alert.type === 'warning' ? 'border-yellow-200 bg-yellow-50' :
                                    alert.type === 'error' ? 'border-red-200 bg-red-50' :
                                        alert.type === 'info' ? 'border-blue-200 bg-blue-50' :
                                            'border-gray-200 bg-gray-50'
                                    }`}
                                onClick={() => {
                                    if (alert.targetPath) {
                                        navigate(alert.targetPath)
                                    }
                                }}
                            >
                                <button
                                    type="button"
                                    className="absolute top-1 right-1 text-slate-400 hover:text-slate-600 text-xs"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        dismiss(alert.id)
                                    }}
                                >
                                    ✕
                                </button>
                                <div className="flex items-start gap-1.5 pr-5">
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${alert.type === 'warning' ? 'bg-yellow-200 text-yellow-800' :
                                        alert.type === 'error' ? 'bg-red-200 text-red-800' :
                                            alert.type === 'info' ? 'bg-blue-200 text-blue-800' :
                                                'bg-gray-200 text-gray-800'
                                        }`}>
                                        {(() => {
                                            const AlertIcon = ALERT_ICON_MAP[alert.type] || Bell
                                            return <AlertIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                        })()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-xs">{alert.title}</div>
                                        <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{alert.message}</div>
                                        {alert.details && (
                                            <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                                                {alert.details}
                                            </div>
                                        )}
                                        <div className="text-xs text-slate-400 mt-0.5">
                                            {formatDateTimeMinute(alert.createdAt)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </DashboardSection>
            )}

            <div className="space-y-4">
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[35fr_65fr]">
                    <div ref={briefingSectionRef} className="min-w-0 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                            <h2 className="text-base font-semibold text-slate-900">오늘의 업무 브리핑</h2>
                            <button
                                type="button"
                                className="text-sm text-brand-sky hover:underline whitespace-nowrap"
                                onClick={() => dailyBriefingQuery.refetch()}
                            >
                                새로고침
                            </button>
                        </div>
                        {dailyBriefingQuery.isError ? (
                            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                                오늘의 업무 브리핑을 불러오지 못했습니다.
                            </div>
                        ) : (
                            <DailyWorkBriefingPanel briefing={dailyBriefingQuery.data} />
                        )}
                    </div>
                    <div className="min-w-0 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h2 className="text-base font-semibold text-slate-900">일정</h2>
                            <div className="flex flex-wrap items-center gap-3">
                                <FavoriteMenuSection toolbarOnly />
                                <Link to="/work/calendar" className="text-sm text-brand-sky hover:underline whitespace-nowrap">
                                    일정관리 →
                                </Link>
                            </div>
                        </div>
                        <DashboardCalendarPanel />
                    </div>
                </div>
            </div>

            <section className="grid grid-cols-1 gap-8 pt-2 xl:grid-cols-2">
                <div className="min-w-0 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                        <h2 className="text-base font-semibold text-slate-900">운영자 공지</h2>
                        <Link to="/ops/system-notices" className="text-sm text-brand-sky hover:underline">
                            더보기 →
                        </Link>
                    </div>
                    {systemNoticesQuery.isLoading ? (
                        <div className="text-sm text-slate-500">로딩 중...</div>
                    ) : systemNotices.length === 0 ? (
                        <div className="text-sm text-slate-500">운영자 공지가 없습니다.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {systemNotices.slice(0, DASHBOARD_HOME_LIST_LIMIT).map((notice) => (
                                <li key={notice.id}>
                                    <Link
                                        to={`/ops/system-notices/${notice.id}`}
                                        className="flex w-full items-center gap-3 rounded px-0.5 py-2.5 text-left transition hover:bg-slate-50"
                                    >
                                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${getSystemNoticeCategoryBadgeClass(notice.category)}`}>
                                            {getSystemNoticeCategoryLabel(notice.category)}
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">
                                            {notice.title || '(제목 없음)'}
                                        </span>
                                        <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-slate-500">
                                            {notice.createdAt ? formatDate(notice.createdAt) : '-'}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="min-w-0 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                        <h2 className="text-base font-semibold text-slate-900">이달의 생보/손보 카드뉴스</h2>
                        {canUseInsurerNewsletters ? (
                            <Link to="/work/insurer-newsletters" className="text-sm text-brand-sky hover:underline">
                                소식지 →
                            </Link>
                        ) : null}
                    </div>
                    <div className="text-xs text-slate-500">
                        <span>{monthlyCardNewsPeriodLabel} 기준</span>
                    </div>
                    <DashboardMonthlyCardNews query={monthlyCardNewsQuery} periodLabel={monthlyCardNewsPeriodLabel} />
                </div>
            </section>

            {/* 기존 관리자용 상세 대시보드 (접을 수 있도록) */}
            {isAdminView && false && (
                <details className="rounded-lg border bg-white p-4">
                    <summary className="cursor-pointer text-lg font-semibold mb-3">상세 대시보드</summary>
                    
                    {/* 조직 선택: 현재 기준 조직 */}
                    <div className="mt-4 flex items-center gap-2">
                        <span className="text-sm text-slate-700">조직 선택</span>
                        <select
                            className="rounded border px-3 py-2 text-sm"
                            value={orgId || ''}
                            onChange={(e) => setOrgId(e.target.value || '')}
                        >
                            <option value="">전체</option>
                            {orgOptions.map(o => (
                                <option key={o.id} value={o.id}>
                                    {o.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 의뢰 상태 요약 섹션 */}
                    <div className="rounded border p-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">의뢰 상태 요약</h3>
                            <p className="text-xs text-slate-500">현재 상태별 의뢰 개수 요약</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                            <div className="rounded bg-slate-50 p-3">
                                <div className="text-xs font-medium text-slate-600">총 의뢰</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.total}</div>
                            </div>
                            <div className="rounded bg-amber-50 p-3">
                                <div className="text-xs font-medium text-amber-700">대기</div>
                                <div className="mt-1 text-2xl font-semibold text-amber-800">{kpi.pending}</div>
                            </div>
                            <div className="rounded bg-sky-50 p-3">
                                <div className="text-xs font-medium text-sky-700">진행중</div>
                                <div className="mt-1 text-2xl font-semibold text-sky-800">{kpi.inProgress}</div>
                            </div>
                            <div className="rounded bg-emerald-50 p-3">
                                <div className="text-xs font-medium text-emerald-700">완료율</div>
                                <div className="mt-1 text-2xl font-semibold text-emerald-800">{kpi.completionRate}%</div>
                            </div>
                        </div>
                    </div>

                    {/* 공지 요약 */}
                    <div className="rounded border p-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">공지</h3>
                            <p className="text-xs text-slate-500">우리조직/원수사 공지</p>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                            <div className="rounded bg-slate-50 p-3">
                                <div className="text-sm font-semibold text-slate-800">우리조직 내 공지</div>
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        className="rounded bg-white p-3 text-left hover:bg-slate-50 border"
                                        onClick={() => navigate(myNoticeOrgType ? `/notices?noticeOrgType=${myNoticeOrgType}&status=APPROVED` : '/notices')}
                                    >
                                        <div className="text-xs font-medium text-slate-600">신규 공지(미읽음)</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{noticeInternalUnreadCount}</div>
                                    </button>
                                    {canApproveNotice && (
                                        <button
                                            type="button"
                                            className="rounded bg-white p-3 text-left hover:bg-slate-50 border"
                                            onClick={() => navigate(myNoticeOrgType ? `/notices?noticeOrgType=${myNoticeOrgType}&status=DRAFT` : '/notices?status=DRAFT')}
                                        >
                                            <div className="text-xs font-medium text-slate-600">승인 필요 공지</div>
                                            <div className="mt-1 text-2xl font-semibold text-slate-900">{noticeApprovalNeededCount}</div>
                                        </button>
                                    )}
                                </div>
                            </div>
                            {(noticeInsurerUnreadCount > 0 || isGaAdmin || isGaPlannerView) && (
                                <div className="rounded bg-slate-50 p-3">
                                    <div className="text-sm font-semibold text-slate-800">원수사 공지</div>
                                    <div className="mt-3 grid grid-cols-1 gap-3">
                                        <button
                                            type="button"
                                            className="rounded bg-white p-3 text-left hover:bg-slate-50 border"
                                            onClick={() => navigate('/notices?noticeOrgType=INSURER&status=APPROVED')}
                                        >
                                            <div className="text-xs font-medium text-slate-600">신규 공지(미읽음)</div>
                                            <div className="mt-1 text-2xl font-semibold text-slate-900">{noticeInsurerUnreadCount}</div>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* GA 사용자는 KPI/상세지표/의뢰추이 노출 제외 */}
                    {!isGaUser && (
                        <>
                            {/* 요약 KPI 섹션 */}
                            <div className="rounded border p-4 mt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-semibold">요약 KPI</h3>
                                    <p className="text-xs text-slate-500">선택된 조직의 주간 의뢰 처리 현황 요약</p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                                    <div className="rounded bg-slate-50 p-3">
                                        <div className="text-xs font-medium text-slate-600">신규 의뢰</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.new}</div>
                                    </div>
                                    <div className="rounded bg-slate-50 p-3">
                                        <div className="text-xs font-medium text-slate-600">보완 요청</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.fix}</div>
                                    </div>
                                    <div className="rounded bg-slate-50 p-3">
                                        <div className="text-xs font-medium text-slate-600">완료</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.done}</div>
                                    </div>
                                    <div className="rounded bg-slate-50 p-3">
                                        <div className="text-xs font-medium text-slate-600">평균 처리기간</div>
                                        <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.avgDays}일</div>
                                    </div>
                                </div>
                            </div>

                            {/* 상세 지표 (주간) */}
                            <div className="rounded border p-4 mt-6">
                                <h3 className="text-lg font-semibold mb-4">상세 지표 (주간)</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div className="text-center p-3 bg-slate-50 rounded">
                                        <div className="text-slate-600">처리율</div>
                                        <div className="text-xl font-semibold mt-1">{kpi.completionRate}%</div>
                                    </div>
                                    <div className="text-center p-3 bg-slate-50 rounded">
                                        <div className="text-slate-600">평균 처리일</div>
                                        <div className="text-xl font-semibold mt-1">{kpi.avgDays}일</div>
                                    </div>
                                    <div className="text-center p-3 bg-slate-50 rounded">
                                        <div className="text-slate-600">진행률</div>
                                        <div className="text-xl font-semibold mt-1">
                                            {kpi.total !== '-' ? Math.round(((Number(kpi.inProgress) + Number(kpi.completed)) / Number(kpi.total)) * 100) : 0}%
                                        </div>
                                    </div>
                                    <div className="text-center p-3 bg-slate-50 rounded">
                                        <div className="text-slate-600">보완률</div>
                                        <div className="text-xl font-semibold mt-1">
                                            {kpi.total !== '-' ? Math.round((Number(kpi.fix) / Number(kpi.total)) * 100) : 0}%
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 트렌드/분포 (주간) = 의뢰추이 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        {/* 시계열 차트 */}
                        <div className="rounded border p-4 h-72">
                            <h3 className="text-lg font-semibold mb-4">의뢰 추이 (주간)</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={series}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Area type="monotone" dataKey="newCount" stackId="1" stroke="#1E3A8A" fill="#1E3A8A" fillOpacity={0.6} />
                                    <Area type="monotone" dataKey="doneCount" stackId="1" stroke="#38BDF8" fill="#38BDF8" fillOpacity={0.6} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        {/* 파이 차트 */}
                        <div className="rounded border p-4 h-72">
                            <h3 className="text-lg font-semibold mb-4">상태별 분포</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                        </>
                    )}
                </details>
            )}

            {/* 2) GA 설계사용 레이아웃 */}
            {isGaPlannerView && false && (
                <>
                    {/* 내 의뢰 요약 섹션 */}
                    <div className="rounded border p-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">내 의뢰 요약</h3>
                            <p className="text-xs text-slate-500">현재 주간 기준 내 의뢰 상태 요약</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div className="rounded bg-slate-50 p-3">
                                <div className="text-xs font-medium text-slate-600">내 의뢰 - 신규/보완</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.new} / {kpi.fix}</div>
                            </div>
                            <div className="rounded bg-sky-50 p-3">
                                <div className="text-xs font-medium text-sky-700">내 의뢰 - 진행중</div>
                                <div className="mt-1 text-2xl font-semibold text-sky-800">{kpi.inProgress}</div>
                            </div>
                            <div className="rounded bg-emerald-50 p-3">
                                <div className="text-xs font-medium text-emerald-700">내 의뢰 - 완료</div>
                                <div className="mt-1 text-2xl font-semibold text-emerald-800">{kpi.done}</div>
                            </div>
                        </div>
                    </div>

                    {/* 내 알림 */}
                    {alerts.length > 0 && (
                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold">내 알림</h3>
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2 text-xs text-slate-600">
                                        <button
                                            type="button"
                                            className="px-2 py-1 rounded border disabled:opacity-40"
                                            disabled={alertPage === 0}
                                            onClick={() => setAlertPage((p) => Math.max(0, p - 1))}
                                        >
                                            이전
                                        </button>
                                        <span>{alertPage + 1} / {totalPages}</span>
                                        <button
                                            type="button"
                                            className="px-2 py-1 rounded border disabled:opacity-40"
                                            disabled={alertPage >= totalPages - 1}
                                            onClick={() => setAlertPage((p) => Math.min(totalPages - 1, p + 1))}
                                        >
                                            다음
                                        </button>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {pagedAlerts.map(alert => (
                                    <div
                                        key={alert.id}
                                        className="relative rounded border p-4 cursor-pointer transition hover:shadow-sm border-blue-100 bg-blue-50"
                                        onClick={() => {
                                            if (alert.targetPath) {
                                                navigate(alert.targetPath)
                                            }
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className="absolute top-2 right-2 text-xs text-slate-400 hover:text-slate-700"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                dismiss(alert.id)
                                            }}
                                        >
                                            ×
                                        </button>
                                        <div className="flex-1">
                                            <div className="font-medium text-sm">{alert.title}</div>
                                            <div className="text-xs text-slate-600 mt-1">{alert.message}</div>
                                            <div className="text-xs text-slate-400 mt-2">
                                                {formatDateTimeMinute(alert.createdAt)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 상태별 분포 */}
                    <div className="rounded border p-4 h-72 mt-6">
                        <h3 className="text-lg font-semibold mb-4">상태별 분포</h3>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </>
            )}

            {/* 2) GA 설계사용 레이아웃 */}
            {isGaPlannerView && false && (
                <>
                    {/* 내 의뢰 요약 섹션 */}
                    <div className="rounded border p-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">내 의뢰 요약</h3>
                            <p className="text-xs text-slate-500">현재 주간 기준 내 의뢰 상태 요약</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                            <div className="rounded bg-slate-50 p-3">
                                <div className="text-xs font-medium text-slate-600">내 의뢰 - 신규/보완</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.new} / {kpi.fix}</div>
                            </div>
                            <div className="rounded bg-sky-50 p-3">
                                <div className="text-xs font-medium text-sky-700">내 의뢰 - 진행중</div>
                                <div className="mt-1 text-2xl font-semibold text-sky-800">{kpi.inProgress}</div>
                            </div>
                            <div className="rounded bg-emerald-50 p-3">
                                <div className="text-xs font-medium text-emerald-700">내 의뢰 - 완료</div>
                                <div className="mt-1 text-2xl font-semibold text-emerald-800">{kpi.done}</div>
                            </div>
                        </div>
                    </div>

                    {/* 상태별 분포 */}
                    <div className="rounded border p-4 h-72 mt-6">
                        <h3 className="text-lg font-semibold mb-4">상태별 분포</h3>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </>
            )}

            {/* 3) 원수사 매니저용 레이아웃 */}
            {isInsManagerView && false && (
                <>
                    {/* 내 의뢰 요약 섹션 */}
                    <div className="rounded border p-4 mt-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-semibold">내 의뢰 요약</h3>
                            <p className="text-xs text-slate-500">현재 주간 기준 담당 의뢰 상태 요약</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                            <div className="rounded bg-slate-50 p-3">
                                <div className="text-xs font-medium text-slate-600">신규 의뢰</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">{kpi.new}</div>
                            </div>
                            <div className="rounded bg-rose-50 p-3">
                                <div className="text-xs font-medium text-rose-700">보완</div>
                                <div className="mt-1 text-2xl font-semibold text-rose-800">{kpi.fix}</div>
                            </div>
                            <div className="rounded bg-sky-50 p-3">
                                <div className="text-xs font-medium text-sky-700">진행중</div>
                                <div className="mt-1 text-2xl font-semibold text-sky-800">{kpi.inProgress}</div>
                            </div>
                            <div className="rounded bg-emerald-50 p-3">
                                <div className="text-xs font-medium text-emerald-700">완료</div>
                                <div className="mt-1 text-2xl font-semibold text-emerald-800">{kpi.done}</div>
                            </div>
                        </div>
                    </div>

                    {/* 의뢰 추이(주간) + 상태별 분포 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        <div className="rounded border p-4 h-72">
                            <h3 className="text-lg font-semibold mb-4">의뢰 추이 (주간)</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={series}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="date" />
                                    <YAxis />
                                    <Tooltip />
                                    <Area type="monotone" dataKey="newCount" stackId="1" stroke="#1E3A8A" fill="#1E3A8A" fillOpacity={0.6} />
                                    <Area type="monotone" dataKey="doneCount" stackId="1" stroke="#38BDF8" fill="#38BDF8" fillOpacity={0.6} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="rounded border p-4 h-72">
                            <h3 className="text-lg font-semibold mb-4">상태별 분포</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* 상세 지표 (주간) */}
                    <div className="rounded border p-4 mt-6">
                        <h3 className="text-lg font-semibold mb-4">상세 지표 (주간)</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="text-center p-3 bg-slate-50 rounded">
                                <div className="text-slate-600">처리율</div>
                                <div className="text-xl font-semibold mt-1">{kpi.completionRate}%</div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 rounded">
                                <div className="text-slate-600">평균 처리일</div>
                                <div className="text-xl font-semibold mt-1">{kpi.avgDays}일</div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 rounded">
                                <div className="text-slate-600">진행률</div>
                                <div className="text-xl font-semibold mt-1">
                                    {kpi.total !== '-' ? Math.round(((Number(kpi.inProgress) + Number(kpi.completed)) / Number(kpi.total)) * 100) : 0}%
                                </div>
                            </div>
                            <div className="text-center p-3 bg-slate-50 rounded">
                                <div className="text-slate-600">보완률</div>
                                <div className="text-xl font-semibold mt-1">
                                    {kpi.total !== '-' ? Math.round((Number(kpi.fix) / Number(kpi.total)) * 100) : 0}%
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </section>
    )
}
